<template>
  <div class="bg-gray-50 m-3 w-3/4 rounded-2xl relative select-none">
    <IconDropdownMenu
      v-if="!this.transit.is_published"
      class="absolute top-2 end-2 min-w-max h-14 z-10"
      :comboOptions="this.dropdownOptions"
      buttonIcon="kebabIcon"
      @update:selection-changed="doTransitCardAction"/>
    <div class="px-3 py-4 flex gap-3 justify-left relative">
      <CardLabel :cardLabelText="getLocationName()" :bold="true"/>
      <div class="flex flex-col gap-2 py-3">
        <div class="flex justify-center items-start gap-2">
          <img :src="this.locationInfo?.icon" class="w-14 h-14"
            alt="transit type icon" @click="copyTransitId">
        </div>
        <div class="flex flex-col text-left text-xs whitespace-nowrap place-items-center">
          <span class="flex gap-1">
            <strong>{{ this.$t('transitCard.date') }}</strong>{{ getLocalDateFromUtcISO8601(transit.start_time) }}
          </span>
          <span class="flex gap-1">
            <strong>{{ this.$t('transitCard.time') }}</strong>{{ getLocalTimeFromUtcISO8601(transit.start_time) }}
          </span>
          <span class="flex gap-1">
            <strong>{{ this.$t('transitCard.duration') }}</strong>{{ getTransitDuration(transit.start_time, transit.end_time) }}
          </span>
          <span class="flex gap-1" v-if="transit.direction && transit.direction !== 'NONE'">
            <strong>{{ this.$t('transitCard.direction') }}</strong>{{ this.$t(`transitCard.${transit.direction}`) }}
          </span>
        </div>
      </div>
      <div class="flex flex-col gap-2 justify-center border border-dotted border-gray-400 rounded-xl py-3 px-2">
        <button
          class="w-9 h-9 rounded-md flex justify-center items-center
            border bg-gray-200 transition-colors duration-200 ease-in-out transform p-0.5
            hover:bg-gray-300 hover:shadow-lg hover:scale-105
            disabled:bg-gray-200 disabled:shadow-none disabled:scale-100"
          :disabled="!this.transitHasImages"
          v-on:click="this.emitter.emit('click-on-image', { transit: this.transit, sections: this.sections, isTrain: this.isTrainTransit() })"
          title="Show image viewer">
          <img
            class="w-7 h-7"
            :src="getIcon(this.transitHasImages ? 'forensicImageIcon' : 'forensicImageDisabledIcon')"
            alt="open popupImage"/>
        </button>
        <button
          class="w-9 h-9 rounded-md flex justify-center items-center
            border bg-gray-200 transition-colors duration-200 ease-in-out transform p-0.5
            hover:bg-gray-300 hover:shadow-lg hover:scale-105
            disabled:bg-gray-200 disabled:shadow-none disabled:scale-100"
          :disabled="!this.transitHasVideos"
          v-on:click="this.emitter.emit('click-on-video', { transit: this.transit, sections: this.sections, isTrain: this.isTrainTransit() })"
          title="Show video player">
          <img
            class="w-7 h-7"
            :src="getIcon(this.transitHasVideos ? 'forensicVideoIcon' : 'forensicVideoDisabledIcon')"
            alt="open popupVideo"/>
        </button>
      </div>
      <div v-if="isTrainTransit()">
        <TransitSummary :transit_summary="transit_summary" />
      </div>
      <div v-else class="flex gap-3 overflow-auto">
        <CardSection
          v-for="(section, index) in sections"
          :key="section.section_id"
          :section="section"
          :sectionIndex="index"
          @update-value="updateSectionValue"
        />
      </div>
    </div>
    <div v-if="showWagons">
      <button @click="toggleShowWagons"
      class="w-full text-gray-500 rounded-t-lg
      border-x border-t border-x-gray-200 border-b-none text-center
      bg-gradient-to-b from-gray-200 to-white
      hover:from-gray-300 via-30% via-gray-300 hover:to-white pb-2">
      {{ $t('transitCard.lessInfo') }}
      </button>
      <WagonSection
       :sections="sections"
       :transit_id="this.transit.id"
       @transit-edited="(data) => this.$emit('transit-edited', data)"/>
    </div>
    <div v-if="isTrainTransit()">
      <button @click="toggleShowWagons"
      class="w-full text-gray-500 bg-gray-200 hover:bg-gray-300 rounded-b-lg shadow-lg
      text-center">{{showWagons ? $t('transitCard.lessInfo') : $t('transitCard.moreInfo')}}</button>
    </div>
  </div>
</template>

<script>
import useTransitStore from '@/stores/TransitStore';
import valueToPercent from '@/utils/math';
import { VEHICLE_INFO_TYPES } from '@/utils/types';
import { getIconName, getIcon } from '@/utils/icons';
import { getLocalDateFromUtcISO8601, getLocalTimeFromUtcISO8601, getDuration } from '@/utils/date';
import { getStatisticalTypeName } from '@/utils/lookup';
import IconDropdownMenu from '@/components/generic/IconDropdownMenu.vue';
import CardSection from './CardSection.vue';
import WagonSection from './WagonSection.vue';
import TransitSummary from './TransitSummary.vue';
import CardLabel from './CardLabel.vue';

const sectionTypeOrder = [
  'vehicle',
  'vehicleclassification',
  'vehicledetection',
  'license plate',
  'trailer license plate',
  'universalcontainer',
  'BIC',
  'adr',
  'containertare',
  'sealreader',
  'sealdetector',
  'vehicleinformation',
  'vehicle information',
];

const excludedClassificationTypes = [
  'movement_direction',
];

export default {
  name: 'TransitCard',
  props: ['transit'],
  emits: ['transit-edited', 'transit-published'],
  components: {
    CardSection,
    WagonSection,
    TransitSummary,
    CardLabel,
    IconDropdownMenu,
  },
  setup() {
    return {
      transitStore: useTransitStore(),
      getLocalDateFromUtcISO8601,
      getLocalTimeFromUtcISO8601,
      getTransitDuration: getDuration,
      getIcon,
    };
  },
  data() {
    return {
      vehicleInfoTypes: VEHICLE_INFO_TYPES,
      sections: [],
      locationInfo: {},
      showWagons: false,
      transit_summary: {},
      dropdownOptions: [
        {
          id: 'resend-transit',
          name: this.$t('transitCard.reprocessTransit'),
          iconName: 'republishTransitIcon',
        },
      ],
    };
  },
  methods: {
    getLocationName() {
      return this.locationInfo.alias ? this.locationInfo.alias : this.locationInfo.name.replaceAll('_', ' ');
    },
    isTrainTransit() {
      return this.locationInfo.transit_type === 'train';
    },
    toggleShowWagons() {
      this.showWagons = !this.showWagons;
    },
    getSection(metaObject) {
      let section = {
        section_id: 0,
        type: '',
        typeConfidence: '',
        icon: '',
        values: [],
        childMetaObjects: [],
        transitHasImages: false,
        transitHasVideos: false,
      };
      section = this.setSectionTypeAndIcon(metaObject, section);
      section = this.setSectionValues(metaObject, section);
      section = this.setDetectionAndImages(metaObject, section);
      return section;
    },
    getFlatChildSectionsList(childMetaObjects) {
      let metaObjectsList = [];
      if (childMetaObjects === null
      || typeof childMetaObjects === 'undefined'
      || childMetaObjects.length === 0) {
        return [];
      }
      childMetaObjects.forEach((childMetaObject) => {
        const noChildrenChildMetaObject = { ...childMetaObject };
        delete noChildrenChildMetaObject.child_meta_objects;
        metaObjectsList.push(noChildrenChildMetaObject);
        metaObjectsList = metaObjectsList.concat(this.getFlatChildSectionsList(childMetaObject.child_meta_objects));
      });
      return metaObjectsList;
    },
    getClassificationIndexExcludingTypes(classifications) {
      if (classifications == null) return -1;

      for (let classIndex = 0; classIndex < classifications.length; classIndex += 1) {
        if (!excludedClassificationTypes.includes(classifications[classIndex].type)) {
          return classIndex;
        }
      }
      return -1;
    },
    // TP-750 / Thiago's feedback: intermodal_marking_rail plates come in two
    // physically different layouts (EN 13044-2 Swap Body/Container vs EN 13044-3
    // Semi-Trailer), distinguishable by their classification type prefix
    // (cs_* vs pn_*, see ars/wrappers/intermodal_marking_rail.py). The panel
    // header must reflect whichever one was actually read, never a generic label.
    getIntermodalMarkingRailHeader(metaObject, type) {
      if (type !== 'intermodal_marking_rail') return null;
      const classificationTypes = metaObject?.classifications?.map((c) => c.type) ?? [];
      if (classificationTypes.some((t) => t?.startsWith('cs_'))) {
        return 'objectCard.intermodalMarkingRailSwapBodyHeader';
      }
      if (classificationTypes.some((t) => t?.startsWith('pn_'))) {
        return 'objectCard.intermodalMarkingRailSemiTrailerHeader';
      }
      return null;
    },
    // TP-750 / Thiago's field mapping table: fields 2-4 differ in *meaning*
    // between the two plate types (e.g. cs_box_width is a physical width,
    // pn_seating_device_height is a hitch height), so the label must be
    // resolved from the classification's own type, never from a fixed
    // 4-label template. The gauge code (field 1) is the plate's reading
    // itself (C+S or P + rail profile), not a classification. box_resistance
    // is genuinely the same "Structure Code" concept on both plate types.
    getIntermodalMarkingRailFieldLabel(type) {
      const labelKeyByType = {
        intermodal_marking_rail: 'objectCard.intermodalMarkingRailGaugeCode',
        cs_box_length: 'objectCard.intermodalMarkingRailLengthCode',
        cs_box_width: 'objectCard.intermodalMarkingRailMaxWidth',
        cs_box_resistance: 'objectCard.intermodalMarkingRailStructureCode',
        pn_seating_device_height: 'objectCard.intermodalMarkingRailHitchHeight',
        pn_box_resistance: 'objectCard.intermodalMarkingRailStructureCode',
        pn_compatibilities: 'objectCard.intermodalMarkingRailCompatibilityCode',
      };
      return labelKeyByType[type] ?? null;
    },
    setSectionTypeAndIcon(metaObject, section) {
      const newSection = { ...section };
      const readingType = metaObject?.reading?.type;
      const classIndex = this.getClassificationIndexExcludingTypes(metaObject?.classifications);
      const classificationType = metaObject?.classifications?.[classIndex]?.type;
      const classificationValue = metaObject?.classifications?.[classIndex]?.value;
      newSection.type = readingType ?? classificationType ?? '';

      if (newSection.type === 'license plate' && classificationValue.includes('trailer')) newSection.type = 'trailer license plate';
      newSection.typeConfidence = metaObject?.reading?.confidence ?? metaObject?.classifications?.[classIndex]?.confidence ?? '';
      newSection.icon = getIconName(newSection.type, classificationValue);
      newSection.customHeader = this.getIntermodalMarkingRailHeader(metaObject, newSection.type);
      if (newSection.type === 'vehicleclassification'
       || newSection.type === 'vehicledetection') newSection.type = 'vehicle';
      if (this.vehicleInfoTypes.includes(newSection.type)) newSection.type = 'vehicle information';

      return newSection;
    },
    setSectionValues(metaObject, section) {
      const newSection = { ...section };
      newSection.section_id = metaObject.id;
      newSection.values = [];
      metaObject?.classifications?.forEach((classification) => {
        const content = {
          db_id: classification?.id,
          value: classification?.value?.replaceAll('_', ' ') ?? '',
          value_type: 'classification',
          confidence: classification?.confidence ?? '',
          edited: classification?.edited,
          check_digit_passed: true,
          label: this.getIntermodalMarkingRailFieldLabel(classification?.type),
        };
        newSection.values.push(content);
      });
      if (metaObject?.reading) {
        const content = {
          db_id: metaObject.reading.id,
          value: metaObject.reading.value?.replaceAll('_', ' ') ?? '',
          value_type: 'reading',
          confidence: metaObject.reading.confidence ?? '',
          edited: metaObject.reading.edited,
          check_digit_passed: true,
          check_digit_str: '',
          label: this.getIntermodalMarkingRailFieldLabel(metaObject.reading.type),
        };
        metaObject.reading.validations?.forEach((validation) => {
          if (validation.value === 1) {
            content.check_digit_passed = false;
            content.check_digit_str
            += this.$t('transitCard.checkDigitFailed', { validationType: validation.type, confidence: valueToPercent(content.confidence) });
          }
        });
        newSection.values.push(content);
      }

      return newSection;
    },
    setDetectionAndImages(metaObject, section) {
      const newSection = { ...section };

      let detectionTime = 0;
      let detectionRoi = {};
      let detectionSourceId = '';
      let detectionTypeName = '';
      let additionalStreamImages = [];

      const getImageObject = (image, selectedCriteria) => (
        {
          criteria: selectedCriteria,
          time: detectionTime,
          roi: (image.stream_id === detectionSourceId) ? detectionRoi : null,
          id: image.id,
          stream_id: image.stream_id,
          stream_alias: image.stream_alias ? image.stream_alias : image.stream_name,
          width: image.width,
          height: image.height,
        }
      );

      const processAdditionalStreamImages = (image) => {
        image.criteria_list?.forEach((selectedCriteria) => {
          if (selectedCriteria !== 'additional_stream') return;
          additionalStreamImages.push(getImageObject(image, selectedCriteria));
        });
      };

      const processImages = (image) => {
        this.transitHasImages = true;
        image.criteria_list?.forEach((selectedCriteria) => {
          if (selectedCriteria === 'additional_stream') return;
          const imageObject = { ...getImageObject(image, selectedCriteria) };
          imageObject.detectionTypeName = detectionTypeName;
          imageObject.additionalStreamImages = [...additionalStreamImages];
          newSection.images.push(imageObject);
        });
      };

      const processDetection = (metaobjectDetection) => {
        detectionTime = metaobjectDetection.time;
        detectionRoi = {
          x: metaobjectDetection.x,
          y: metaobjectDetection.y,
          width: metaobjectDetection.width,
          height: metaobjectDetection.height,
        };
        detectionSourceId = metaobjectDetection.stream_id;
        additionalStreamImages = [];
        metaobjectDetection.images?.forEach(processAdditionalStreamImages);
        metaobjectDetection.images?.forEach(processImages);
      };
      newSection.images = [];

      metaObject?.classifications?.forEach((classification) => {
        detectionTypeName = getStatisticalTypeName(classification.type);
        classification.detections?.forEach(processDetection);
      });

      if (metaObject.reading) {
        detectionTypeName = getStatisticalTypeName(metaObject.reading.type);
        metaObject.reading.detections?.forEach(processDetection);
      }
      newSection.time = detectionTime;
      return newSection;
    },
    async updateSectionValue({ index, updatedValue, sectionIndex }) {
      const currentSection = this.sections[sectionIndex].values[index];
      if (currentSection.value === updatedValue) return;
      let putResult = false;
      if (currentSection.value_type === 'reading') {
        putResult = await this.transitStore.putReadingValue({ readingId: currentSection.db_id, newValue: updatedValue });
      } else {
        putResult = await this.transitStore.putClassificationValue({ classificationId: currentSection.db_id, newValue: updatedValue });
      }
      if (putResult) {
        currentSection.edited = true;
        currentSection.value = updatedValue;
        this.$emit('transit-edited', { transitId: this.transit.id });
        this.emitter.emit('show-toast', { toastType: 'info', toastText: this.$t('transitCard.successfulEdit') });
      } else {
        const replaceValue = currentSection.value;
        currentSection.value = replaceValue;
        this.emitter.emit('show-toast', { toastType: 'error', toastText: this.$t('transitCard.errorOnEdit') });
      }
    },
    updateSection() {
      this.sections = [];
      this.transitHasImages = false;
      this.transit?.meta_objects?.forEach((metaObject) => {
        const section = this.getSection(metaObject);
        const childMetaObjectsList = this.getFlatChildSectionsList(metaObject.child_meta_objects);
        childMetaObjectsList.forEach((childMetaObject) => {
          section.childMetaObjects.push(this.getSection(childMetaObject));
        });
        this.sections.push(section);
      });
      this.sortSections();
    },
    async doTransitCardAction(actions) {
      if (actions.length <= 0 || actions[0] !== 'resend-transit') {
        return;
      }
      await this.transitStore.republishTransit(this.transit.id);
      this.$emit('transit-published', { transitId: this.transit.id });
      this.emitter.emit('show-toast', { toastType: 'info', toastText: this.$t('transitCard.transitReprocessed') });
    },
    sortSections() {
      if (this.isTrainTransit()) return;
      this.sections.sort((section1, section2) => {
        if (sectionTypeOrder.indexOf(section1.type) < sectionTypeOrder.indexOf(section2.type)) {
          return -1;
        }
        if (sectionTypeOrder.indexOf(section1.type) > sectionTypeOrder.indexOf(section2.type)) {
          return 1;
        }
        if (section1.section_id < section2.section_id) {
          return -1;
        }
        if (section1.section_id > section2.section_id) {
          return 1;
        }
        return 0;
      });
    },
    updateLocationInfo() {
      this.locationInfo = this.transitStore.locations.find((location) => location.id === this.transit.location_id);
      if (!this.locationInfo) {
        return;
      }
      this.locationInfo.icon = getIcon(getIconName(this.locationInfo.transit_type));
    },
    updateSummaryInfo() {
      if (!this.isTrainTransit()) return;
      this.transit_summary = {
        wagons: 0,
        containers: 0,
        adr: 0,
        direction: '',
      };
      this.transit_summary.direction = this.transit.direction;
      this.sections.forEach((singleSection) => {
        if (singleSection.type === 'universalwagon') {
          this.transit_summary.wagons += 1;
          singleSection.childMetaObjects.forEach((childSection) => {
            if (childSection.type.toUpperCase() === 'BIC') this.transit_summary.containers += 1;
            if (childSection.type.toUpperCase() === 'ADR') this.transit_summary.adr += 1;
          });
        }
      });
    },
    copyTransitId() {
      navigator.clipboard.writeText(this.transit.id);
    },
    updateTransitInformation() {
      this.updateLocationInfo();
      this.updateSection();
      this.updateSummaryInfo();
      this.transitHasVideos = this.transit.videos?.length > 0;
    },
  },
  beforeMount() {
    this.updateTransitInformation();
  },
  watch: {
    transit() {
      this.updateTransitInformation();
    },
  },
};
</script>
